import { Grid, Icon, Tooltip, Card, CardContent } from "@material-ui/core";
import React, { useEffect, useMemo } from "react";
import styles from "./GroupConfiguration.style";
import { GroupDetail } from "../../../models/BatchMessage/BatchMessageGroupDetailModel";
import { useGetComponentStringsContext } from "../../../hooks/useGetComponentStrings";
import { useBatchGroupConfiguration } from "../../../hooks/useBatchGroupConfiguration";
import { GroupItem, IExcel } from "../../../common/batchNotificationInterface";
import { ButtonPrimary, LinkPrimary } from "../../../common/sharedStyle";
import { GroupDetailRow } from "../GroupDetailRow/GroupDetailRow";
import BatchMessageDetailModel from "../../../models/BatchMessage/BatchMessageDetailModel";
import Shared from "../../../common/shared";
import { BatchMessageTypeEnum } from "../../../common/enum";

export interface GroupConfigurationProps {
	batchNotificationDetail: BatchMessageDetailModel;
	onGroupDetailChanged: React.Dispatch<
		React.SetStateAction<BatchMessageDetailModel>
	>;
	isReadOnly: boolean;
	setHasStep3ConfigChanged: React.Dispatch<React.SetStateAction<boolean>>;
	highlightRequiredDetail: boolean;
}

export const GroupConfiguration: React.FC<GroupConfigurationProps> = ({
	batchNotificationDetail,
	isReadOnly,
	onGroupDetailChanged,
	setHasStep3ConfigChanged,
	highlightRequiredDetail,
}) => {
	const classes = styles();
	const strings = useGetComponentStringsContext().GroupConfiguration;

	// Collecting all variables from all messagse to assign values from excel sheet to
	const dynamicVariables = useMemo(() => {
		const variables: string[] = [];
		batchNotificationDetail.batchMessageLanguageDetail?.languages.forEach(
			(language) => {
				const regex = new RegExp(/\[\[\S+\]\]/g);
				const matches = language.message.match(regex);
				if (matches) {
					variables.push(...matches);
				}
			}
		);
		return variables;
	}, [batchNotificationDetail]);

	// use reducer for all state logic
	const [state, dispatch] = useBatchGroupConfiguration();

	useEffect(() => {
		onGroupDetailChanged((prevBatchNotificationDetail) => {
			return {
				...prevBatchNotificationDetail,
				batchMessageGroupDetail: {
					...prevBatchNotificationDetail.batchMessageGroupDetail,
					preSubmissionConfigurationId: batchNotificationDetail.id,
					partitionKey: batchNotificationDetail.partitionKey,
					groupDetails: state.items.map((x) => x.groupDetail),
				},
			};
		});
	}, [
		batchNotificationDetail.id,
		batchNotificationDetail.partitionKey,
		onGroupDetailChanged,
		state.items,
	]);

	useEffect(() => {
		let groupDetailList: GroupDetail[];
		const emptyExcelData: IExcel[] = [];
		if (
			batchNotificationDetail &&
			batchNotificationDetail.batchMessageGroupDetail &&
			batchNotificationDetail.batchMessageGroupDetail.groupDetails.length > 0
		) {
			groupDetailList = JSON.parse(
				JSON.stringify(
					batchNotificationDetail.batchMessageGroupDetail.groupDetails
				)
			);
			for (
				let i = 0;
				i < batchNotificationDetail.batchMessageGroupDetail.groupDetails.length;
				i++
			) {
				emptyExcelData.push({
					id: "",
					header: [],
					contentData: [],
					variableMapping: {},
					isHeaderIncluded: false,
					selectedColumn: "Column 1",
				});
			}
		} else {
			const emptyGroupDetail: GroupDetail[] = [new GroupDetail()];
			groupDetailList = emptyGroupDetail;
			emptyExcelData.push({
				id: "",
				header: [],
				contentData: [],
				variableMapping: {},
				isHeaderIncluded: false,
				selectedColumn: "Column 1",
			});
		}
		dispatch({
			type: "INITIALIZE_BATCH_GROUP_DETAILS",
			payload: {
				groupDetailList: groupDetailList,
				excelData: emptyExcelData,
			},
		});
		// disabled eslint rule as useEffect needs to be called once on page load to initialize state
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getExcelDataUsingId = (id: string): IExcel => {
		const index = state.excelData.findIndex((x) => x.id === id);
		return state.excelData[index];
	};

	const showWhatsAppLimitExceededMessage = useMemo(() => {
		const groupItem: GroupItem | undefined = state.items.find(
			(x) => x.groupDetail.phoneNumberCount > Shared.whatsappRateLimit
		);
		return groupItem !== undefined;
	}, [state.items]);

	return (
		<div className={classes.root} data-testid="groupConfiguration">
			<Card variant="outlined">
				<CardContent>
					<Grid container spacing={0}>
						<Grid xs={12} item={true}>
							<div className={classes.addGroupDetail}>
								<Tooltip title={strings.newGroupToolTip}>
									<span>
										<ButtonPrimary
											variant="contained"
											color="primary"
											onClick={() => {
												dispatch({ type: "ADD_NEW_GROUP_ITEM" });
												setHasStep3ConfigChanged(true);
											}}
											className={classes.addGroupDetailBtn}
											disabled={isReadOnly}
											data-testid="addGroupDetailsBtn"
										>
											<Icon>add</Icon>
										</ButtonPrimary>
									</span>
								</Tooltip>
							</div>
						</Grid>
						{state.items && state.items.length > 0
							? state.items.map((e: any, index: number) => (
									<GroupDetailRow
										dynamicVariables={dynamicVariables}
										key={e.rowId}
										excelData={getExcelDataUsingId(e.rowId)}
										item={e}
										isReadOnly={isReadOnly}
										showRemoveRowButton={state.items.length > 1}
										highlightRequiredDetail={highlightRequiredDetail}
										onGroupNameChanged={(name: string) => {
											dispatch({
												type: "SET_GROUP_ITEM_TITLE",
												payload: { id: e.rowId, name },
											});
											setHasStep3ConfigChanged(true);
										}}
										onExcelDataChanged={(excelData: IExcel) => {
											dispatch({
												type: "SET_GROUP_ITEM_EXCEL_DATA",
												payload: {
													id: e.rowId,
													excelData: excelData,
												},
											});
											setHasStep3ConfigChanged(true);
										}}
										onRemoveClicked={() => {
											dispatch({
												type: "REMOVE_GROUP_ITEM",
												payload: {
													id: e.rowId,
												},
											});
											setHasStep3ConfigChanged(true);
										}}
									/>
							  ))
							: ""}
						{batchNotificationDetail.batchMessageType ===
							BatchMessageTypeEnum.WhatsApp &&
							!isReadOnly && (
								<Grid item xs={12} data-testid="infoMessage">
									<div
										className={
											showWhatsAppLimitExceededMessage
												? `${classes.warningMessage} warningMessage`
												: "infoMessage"
										}
									>
										<span>{strings.whatsappRateLimitInfoMessage} </span>
										<LinkPrimary
											href="https://support.twilio.com/hc/en-us/articles/360024008153-WhatsApp-Rate-Limiting"
											target="blank"
										>
											https://support.twilio.com/hc/en-us/articles/360024008153-WhatsApp-Rate-Limiting
										</LinkPrimary>
									</div>
								</Grid>
							)}
					</Grid>
				</CardContent>
			</Card>
		</div>
	);
};
